
import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Mic, Square, Play, Pause, Download, Trash2, Clock } from 'lucide-react';
import { useTwilioCall } from '@/hooks/useTwilioCall';

interface Recording {
  id: string;
  name: string;
  duration: string;
  timestamp: Date;
  size: string;
  callSid?: string;
}

export const CallRecording = () => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [playingId, setPlayingId] = useState<string | null>(null);
  const { callInProgress, callSid } = useTwilioCall();

  // Mock recordings data - in real implementation, this would come from Twilio
  useEffect(() => {
    const mockRecordings: Recording[] = [
      {
        id: '1',
        name: 'Call with John Smith',
        duration: '4:32',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 2),
        size: '1.2 MB',
        callSid: 'CA123456789'
      },
      {
        id: '2',
        name: 'Follow-up call',
        duration: '2:18',
        timestamp: new Date(Date.now() - 1000 * 60 * 60 * 4),
        size: '0.8 MB',
        callSid: 'CA987654321'
      }
    ];
    setRecordings(mockRecordings);
  }, []);

  // Recording timer
  useEffect(() => {
    let interval: number | null = null;
    
    if (isRecording) {
      interval = window.setInterval(() => {
        setRecordingDuration(prev => prev + 1);
      }, 1000);
    } else {
      if (interval) {
        clearInterval(interval);
      }
    }

    return () => {
      if (interval) {
        clearInterval(interval);
      }
    };
  }, [isRecording]);

  const formatDuration = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const startRecording = async () => {
    if (!callInProgress) return;
    
    try {
      // In real implementation, this would start Twilio call recording
      setIsRecording(true);
      setRecordingDuration(0);
      console.log('Started recording for call:', callSid);
    } catch (error) {
      console.error('Failed to start recording:', error);
    }
  };

  const stopRecording = async () => {
    if (!isRecording) return;
    
    try {
      // In real implementation, this would stop Twilio call recording
      setIsRecording(false);
      
      // Add the recording to the list
      const newRecording: Recording = {
        id: Date.now().toString(),
        name: `Call Recording ${new Date().toLocaleTimeString()}`,
        duration: formatDuration(recordingDuration),
        timestamp: new Date(),
        size: `${(recordingDuration * 0.032).toFixed(1)} MB`, // Rough estimate
        callSid: callSid || undefined
      };
      
      setRecordings(prev => [newRecording, ...prev]);
      setRecordingDuration(0);
      
      console.log('Stopped recording for call:', callSid);
    } catch (error) {
      console.error('Failed to stop recording:', error);
    }
  };

  const playRecording = (recordingId: string) => {
    if (playingId === recordingId) {
      setPlayingId(null);
    } else {
      setPlayingId(recordingId);
      // In real implementation, this would play the actual recording
      console.log('Playing recording:', recordingId);
    }
  };

  const downloadRecording = (recording: Recording) => {
    // In real implementation, this would download from Twilio
    console.log('Downloading recording:', recording.id);
    // Mock download
    alert(`Downloading: ${recording.name}`);
  };

  const deleteRecording = (recordingId: string) => {
    setRecordings(prev => prev.filter(r => r.id !== recordingId));
  };

  return (
    <div className="space-y-4">
      {/* Recording Controls */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Mic className="h-4 w-4" />
            Call Recording
            {isRecording && (
              <Badge variant="destructive" className="animate-pulse">
                <div className="w-2 h-2 bg-white rounded-full mr-2" />
                REC
              </Badge>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {callInProgress ? (
            <div className="space-y-3">
              {isRecording ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Recording Duration</span>
                    <Badge variant="outline" className="font-mono">
                      <Clock className="h-3 w-3 mr-1" />
                      {formatDuration(recordingDuration)}
                    </Badge>
                  </div>
                  
                  <Button
                    variant="destructive"
                    onClick={stopRecording}
                    className="w-full"
                  >
                    <Square className="h-4 w-4 mr-2" />
                    Stop Recording
                  </Button>
                </div>
              ) : (
                <Button
                  variant="default"
                  onClick={startRecording}
                  className="w-full"
                >
                  <Mic className="h-4 w-4 mr-2" />
                  Start Recording
                </Button>
              )}
            </div>
          ) : (
            <div className="text-center py-4">
              <Mic className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">
                Start a call to begin recording
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recordings List */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">
            Recent Recordings ({recordings.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <ScrollArea className="h-[300px]">
            <div className="p-3 space-y-3">
              {recordings.length === 0 ? (
                <div className="text-center py-8">
                  <Mic className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                  <p className="text-muted-foreground text-sm">
                    No recordings yet
                  </p>
                </div>
              ) : (
                recordings.map((recording) => (
                  <Card key={recording.id} className="p-3">
                    <div className="space-y-2">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="font-medium text-sm">{recording.name}</div>
                          <div className="text-xs text-muted-foreground">
                            {recording.timestamp.toLocaleString()}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => playRecording(recording.id)}
                          >
                            {playingId === recording.id ? (
                              <Pause className="h-4 w-4" />
                            ) : (
                              <Play className="h-4 w-4" />
                            )}
                          </Button>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0"
                            onClick={() => downloadRecording(recording)}
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            onClick={() => deleteRecording(recording.id)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>Duration: {recording.duration}</span>
                        <span>Size: {recording.size}</span>
                        {recording.callSid && (
                          <span>Call: {recording.callSid.slice(-8)}</span>
                        )}
                      </div>
                    </div>
                  </Card>
                ))
              )}
            </div>
          </ScrollArea>
        </CardContent>
      </Card>
    </div>
  );
};
